/**
 * Attack and defense proficiencies (Foundry: CharacterPF2e#prepareMartialProficiencies and
 * actor/character/helpers.ts): the weapon and armour categories the class Entry trains, plus the
 * custom proficiencies MartialProficiency Rule Elements add. `sameAs` links follow their category's
 * rank (capped by `maxRank`), duplicates collapse, and every proficiency gets its bonus at the
 * Character's Level. Later issues ask this store what rank a Character has with a given weapon
 * (#8) or armour (#11).
 */
import type { MartialProficiencyData } from "./actor-data";
import type { Override } from "../character/model";
import { createProficiencyModifier, signed } from "./modifiers";
import { testPredicate } from "./predicate";
import type { EngineState } from "./state";
import { ARMOR_CATEGORIES, ARMOR_CATEGORY_LABELS, EQUIVALENT_WEAPONS, PROFICIENCY_RANKS, WEAPON_CATEGORIES, WEAPON_CATEGORY_LABELS, clampRank, type ArmorCategory, type Rank, type WeaponCategory } from "./values";

export type ProficiencyKind = "attack" | "defense";

/** The Override key for a proficiency rank: `attack-rank:<slug>` or `defense-rank:<slug>`. */
export function rankOverrideKey(kind: ProficiencyKind, slug: string): string {
  return `${kind}-rank:${slug}`;
}

/** Parse a rank Override key; undefined for any other key. */
export function parseRankOverrideKey(key: string): { kind: ProficiencyKind; slug: string } | undefined {
  const m = /^(attack|defense)-rank:(.+)$/.exec(key);
  return m ? { kind: m[1] as ProficiencyKind, slug: m[2]! } : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isDeepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The label of a category, or of a custom proficiency (its own label, falling back to the slug). */
export function proficiencyLabel(kind: ProficiencyKind, slug: string, record: MartialProficiencyData | undefined): string {
  if (record?.label) return record.label;
  if (kind === "attack" && (WEAPON_CATEGORIES as readonly string[]).includes(slug)) return WEAPON_CATEGORY_LABELS[slug as WeaponCategory];
  if (kind === "defense" && (ARMOR_CATEGORIES as readonly string[]).includes(slug)) return ARMOR_CATEGORY_LABELS[slug as ArmorCategory];
  return slug;
}

/**
 * Apply rank Overrides (`attack-rank:<slug>`, `defense-rank:<slug>`). Foundry leaves class
 * proficiency increases the class features describe but do not encode (a fighter's master rank
 * in simple and martial weapons at 13) to the player's hand on the sheet; here that hand is an
 * Override on the rank, which every dependent statistic then uses. Returns the keys used.
 */
export function applyRankOverrides(state: EngineState, overrides: readonly Override[]): Set<string> {
  const used = new Set<string>();
  for (const override of overrides) {
    const parsed = parseRankOverrideKey(override.statistic);
    if (!parsed) continue;
    const table = state.actor.system.proficiencies[parsed.kind === "attack" ? "attacks" : "defenses"];
    const record = table[parsed.slug];
    if (!record) continue;
    used.add(override.statistic);
    state.derivedRanks[override.statistic] ??= clampRank(record.rank);
    record.rank = clampRank(override.value);
  }
  return used;
}

/**
 * Foundry's prepareMartialProficiencies: link `sameAs` ranks, drop duplicates, set each
 * proficiency's bonus and breakdown, and fill the fields an ActiveEffectLike-created record lacks.
 * Idempotent: the engine runs it after the applyAEs phase (so roll options see the ranks) and
 * again before statistics derive (so beforeDerived changes land).
 */
export function prepareMartialProficiencies(state: EngineState, overrides: readonly Override[] = []): void {
  const { level, variantRules } = state;
  for (const key of ["attacks", "defenses"] as const) {
    const kind: ProficiencyKind = key === "attacks" ? "attack" : "defense";
    const proficiencies = state.actor.system.proficiencies[key];
    for (const [slug, raw] of Object.entries(proficiencies)) {
      if (!isRecord(raw)) {
        delete proficiencies[slug];
        continue;
      }
      raw.rank = clampRank(raw.rank);
      raw.visible ??= true;
      raw.label ??= proficiencyLabel(kind, slug, undefined);
      if (raw.definition && raw.custom === undefined) raw.custom = true;
    }
    // Linked proficiencies follow their category.
    for (const proficiency of Object.values(proficiencies)) {
      if (!proficiency.sameAs || !(proficiency.sameAs in proficiencies)) continue;
      const category = proficiencies[proficiency.sameAs];
      const maxRankIndex = PROFICIENCY_RANKS.indexOf(proficiency.maxRank ?? "legendary");
      proficiency.rank = Math.min(category?.rank ?? 0, maxRankIndex < 0 ? 4 : maxRankIndex) as Rank;
    }
    // A rank Override on a linked proficiency wins over the link; on a category it already applied earlier.
    for (const override of overrides) {
      const parsed = parseRankOverrideKey(override.statistic);
      const record = parsed?.kind === kind ? proficiencies[parsed.slug] : undefined;
      if (!record) continue;
      state.derivedRanks[override.statistic] ??= clampRank(record.rank);
      record.rank = clampRank(override.value);
    }
    // Two custom proficiencies with the same definition and link: keep the higher rank.
    const all = Object.entries(proficiencies);
    for (const [, proficiency] of all) {
      if (!proficiency || !(slugStillPresent(proficiencies, proficiency))) continue;
      const duplicates = all.flatMap(([k, p]) =>
        p && proficiency !== p && proficiency.rank >= p.rank && "definition" in proficiency && "definition" in p && proficiency.sameAs === p.sameAs && isDeepEqual(p.definition ?? [], proficiency.definition ?? []) ? [k] : [],
      );
      for (const duplicate of duplicates) delete proficiencies[duplicate];
    }
    for (const proficiency of Object.values(proficiencies)) {
      const bonus = createProficiencyModifier({ rank: proficiency.rank, level, withoutLevel: variantRules.proficiencyWithoutLevel });
      proficiency.value = bonus.value;
      proficiency.breakdown = `${bonus.label} ${signed(bonus.value)}`;
    }
  }
}

function slugStillPresent(table: Record<string, MartialProficiencyData>, record: MartialProficiencyData): boolean {
  return Object.values(table).includes(record);
}

export interface WeaponLike {
  /** `simple`, `martial`, `advanced` or `unarmed`. */
  category: string | null;
  /** Weapon group slug (`sword`, `bow`, ...). */
  group?: string | null;
  /** Base weapon slug (`longsword`, ...). */
  baseItem?: string | null;
}

/**
 * The rank a Character has with a weapon (Foundry: getWeaponProficiencyRank): the best of its
 * category, a group or base-weapon proficiency, and every custom proficiency whose `definition`
 * the weapon's roll options satisfy (so an ancestral weapon familiarity makes its weapons count
 * as a lower category).
 */
export function weaponProficiencyRank(attacks: Record<string, MartialProficiencyData>, weapon: WeaponLike, itemOptions: Iterable<string>): Rank {
  const options = itemOptions instanceof Set ? itemOptions : new Set(itemOptions);
  const categoryRank = attacks[weapon.category ?? ""]?.rank ?? 0;
  const groupRank = attacks[`weapon-group-${weapon.group ?? ""}`]?.rank ?? 0;
  const base = EQUIVALENT_WEAPONS[weapon.baseItem ?? ""] ?? weapon.baseItem ?? "";
  const baseRank = attacks[`weapon-base-${base}`]?.rank ?? 0;
  const syntheticRanks = Object.values(attacks)
    .filter((p) => !!p?.definition && testPredicate(p.definition, options))
    .map((p) => p.rank);
  return Math.max(categoryRank, groupRank, baseRank, ...syntheticRanks) as Rank;
}

/** The rank a Character has with a worn armour (Foundry: getArmorProficiencyRank); `unarmored` when none is worn. */
export function armorProficiencyRank(defenses: Record<string, MartialProficiencyData>, armor: { category: string | null } | null, itemOptions: Iterable<string> = []): Rank {
  const options = itemOptions instanceof Set ? itemOptions : new Set(itemOptions);
  let best: Rank = 0;
  for (const [key, proficiency] of Object.entries(defenses)) {
    if (!proficiency) continue;
    const matches = !armor ? key === "unarmored" : armor.category === key || (proficiency.definition ? testPredicate(proficiency.definition, options) : false);
    if (matches && proficiency.rank > best) best = proficiency.rank;
  }
  return best;
}
